import { standardPayment } from "./studentLoanMath";
import { round2 } from "../household/moneyInput";

/** Federal direct loan rate for new undergraduate loans, as published for one award year. Update each July when the new rate is announced. */
export const FEDERAL_UNDERGRAD_RATE = { pct: 6.52, awardYear: "2026-27" } as const;

export interface YearRow { year: number; cost: number; aid: number; net: number; aidExceedsCost: boolean }
export interface SchoolPlan { rows: YearRow[]; total: number; aidExceedsYears: number[] }

/**
 * Year-by-year cost of attendance: the first-year cost grows by the annual increase, aid is a flat amount per year and is
 * never worth more than the year's cost (unused aid is not paid out, so a year never goes below $0).
 */
export function schoolPlan(i: { baseAnnual: number; years: number; increasePct: number; aidPerYear: number }): SchoolPlan {
  const rows: YearRow[] = [];
  let total = 0;
  for (let y = 0; y < i.years; y++) {
    const cost = i.baseAnnual * Math.pow(1 + i.increasePct / 100, y);
    const net = Math.max(0, cost - i.aidPerYear);
    total += net;
    rows.push({ year: y + 1, cost, aid: i.aidPerYear, net, aidExceedsCost: i.aidPerYear > cost });
  }
  return { rows, total, aidExceedsYears: rows.filter((r) => r.aidExceedsCost).map((r) => r.year) };
}

export interface Funding {
  familyPerYear: number;
  workStudyPerYear: number;
  ratePct: number;
  termYears: number;
}
export interface LoanOutcome {
  /** Net cost left after family money and work-study, summed over the years: what has to be borrowed. */
  borrowed: number;
  /** Interest that builds up while in school (simple interest, from each year's disbursement to graduation). */
  schoolInterest: number;
  /** Borrowed plus in-school interest: the balance when repayment starts. */
  debtAtGraduation: number;
  monthlyPayment: number;
  repaymentInterest: number;
  totalRepaid: number;
  borrowedByYear: number[];
}

/**
 * Funding gap and loan. Each year's gap (net cost minus family contribution minus work-study, never below 0) is borrowed at the
 * start of that year. Interest builds up as simple interest until graduation and is added to the balance when repayment
 * starts, as on federal unsubsidized loans; the grace period is not counted. The payment is the level payment for the term.
 */
export function loanOutcome(netByYear: number[], f: Funding): LoanOutcome {
  const n = netByYear.length;
  const borrowedByYear = netByYear.map((net) => Math.max(0, net - f.familyPerYear - f.workStudyPerYear));
  const borrowed = borrowedByYear.reduce((a, b) => a + b, 0);
  const schoolInterest = borrowedByYear.reduce((a, b, k) => a + b * (f.ratePct / 100) * (n - k), 0);
  const debtAtGraduation = borrowed + schoolInterest;
  const monthlyPayment = debtAtGraduation > 0 ? standardPayment(debtAtGraduation, f.ratePct, f.termYears) : 0;
  const totalRepaid = round2(monthlyPayment * Math.round(f.termYears * 12));
  return {
    borrowed: round2(borrowed),
    schoolInterest: round2(schoolInterest),
    debtAtGraduation: round2(debtAtGraduation),
    monthlyPayment,
    repaymentInterest: debtAtGraduation > 0 ? round2(Math.max(0, totalRepaid - debtAtGraduation)) : 0,
    totalRepaid: debtAtGraduation > 0 ? totalRepaid : 0,
    borrowedByYear,
  };
}

/** "Year 2", "Years 1 to 3", "Years 1, 3 and 4" for the aid-exceeds-cost note. */
export function yearsPhrase(years: number[]): string {
  if (years.length === 0) return "";
  if (years.length === 1) return `Year ${years[0]}`;
  const consecutive = years.every((y, i) => i === 0 || y === years[i - 1] + 1);
  if (consecutive && years.length > 2) return `Years ${years[0]} to ${years[years.length - 1]}`;
  return `Years ${years.slice(0, -1).join(", ")} and ${years[years.length - 1]}`;
}

export const yearsWord = (n: number) => (n === 1 ? "1 year" : `${n} years`);
